import axios from 'axios';
import authService from './authService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// The frontend is season-specific (player photos, round count assumptions),
// so pin the season explicitly rather than relying on the backend default.
const SEASON = 2026;

const DATASET_KEYS = ['main', 'count1', 'count2', 'count3', 'count4', 'count5', 'betfair', 'espn', 'byb'];

// Guards against a malformed/partial payload hydrating the app with data that
// crashes every table. Only checks structure, not contents.
const isValidPayload = (body) => {
  if (!body || body.success !== true || !body.data) return false;
  const { leaderboard, game_vote_data: gameVoteData } = body.data;
  if (!Array.isArray(leaderboard) || leaderboard.length === 0) return false;
  if (!gameVoteData) return false;
  return DATASET_KEYS.every((key) => Array.isArray(gameVoteData[key]));
};

// Normalises every failure into { success:false, status, code, message, flags }
// so the context can branch without knowing axios internals. Mirrors the error
// contract in bg_api.md.
const normaliseError = (err) => {
  if (err.response) {
    const { status, data: body } = err.response;
    // 401 nests the code under error.code; other statuses put it on error.
    const code =
      (body && body.error && (typeof body.error === 'string' ? body.error : body.error.code)) || null;
    const fallbacks = {
      400: 'Invalid vote data request.',
      401: 'Your session has expired. Please log in again.',
      403: 'Access to Brownlow Genius is required.',
      404: 'Vote data for this season is not available.',
      429: 'Too many requests. Please try again in a minute.',
      500: 'Something went wrong loading vote data. Please try again.',
    };
    return {
      success: false,
      status,
      code,
      message: (body && body.message) || fallbacks[status] || fallbacks[500],
      isAuthError: status === 401,
      isRoleError: status === 403,
      isRateLimited: status === 429,
      isNetworkError: false,
    };
  }
  return {
    success: false,
    status: null,
    code: 'NETWORK_ERROR',
    message: 'Network error. Please check your connection and try again.',
    isAuthError: false,
    isRoleError: false,
    isRateLimited: false,
    isNetworkError: true,
  };
};

const voteDataService = {
  /**
   * GET /api/v1/bg/vote-data — the entire season payload in one response.
   * Resolves to { success:true, season, data } or a normalised error object;
   * never throws.
   */
  fetchVoteData: async (season = SEASON) => {
    try {
      // Browser caching is safe here: the API responds with Cache-Control:
      // private plus Vary: Authorization, so cache entries are keyed per
      // token and a payload cached under an entitled session is never
      // replayed to a different token (see bg_api.md "Caching & compression").
      const response = await axios.get(`${API_URL}/api/v1/bg/vote-data`, {
        params: { season },
        headers: { Authorization: `Bearer ${authService.getToken()}` },
      });
      if (!isValidPayload(response.data)) {
        return {
          success: false,
          status: 200,
          code: 'MALFORMED_PAYLOAD',
          message: 'Vote data response was malformed. Please try again later.',
          isAuthError: false,
          isRoleError: false,
          isRateLimited: false,
          isNetworkError: false,
        };
      }
      return { success: true, season: response.data.season, data: response.data.data };
    } catch (err) {
      return normaliseError(err);
    }
  },
};

export default voteDataService;
